import Phaser from 'phaser';

const MIN_LANE_BOTTOM_WIDTH_PCT = 18;
const MIN_LANE_TOP_WIDTH_PCT = 5;
const MAX_LANE_TOP_WIDTH_PCT = 70;
const LANE_CURVE_DEPTH_RATIO = 0.1;
const LANE_CURVE_POINTS_COUNT = 24;
const MIN_LANE_CONTROL_OFFSET = 2;
const LANE_CONTROL_OFFSET_RATIO = 0.02;

type LaneSide = 'left' | 'right';

export type LaneShapeMetrics = {
	// Total lane drawing width in scene coordinates.
	width: number;
	// Total lane drawing height in scene coordinates.
	height: number;
	// Vertical offset where the lane begins inside the full scene.
	topOffset: number;
	// Shared X center used by the top and bottom lane widths.
	centerX: number;
	// Pixel width of the lane at the top edge.
	topWidthPx: number;
	// Pixel width of the lane at the bottom edge.
	bottomWidthPx: number;
	// Vertical depth of the bezier control point from the lane top.
	curveY: number;
	// Top-left corner of the lane.
	leftTop: Phaser.Math.Vector2;
	// Top-right corner of the lane.
	rightTop: Phaser.Math.Vector2;
	// Bottom-left corner of the lane.
	leftBottom: Phaser.Math.Vector2;
	// Bottom-right corner of the lane.
	rightBottom: Phaser.Math.Vector2;
	// Left bezier control point that shapes the lane edge.
	leftControl: Phaser.Math.Vector2;
	// Right bezier control point that shapes the lane edge.
	rightControl: Phaser.Math.Vector2;
	// Prebuilt left border curve used for drawing and positioning.
	leftCurve: Phaser.Curves.QuadraticBezier;
	// Prebuilt right border curve used for drawing and positioning.
	rightCurve: Phaser.Curves.QuadraticBezier;
};

// Converts a local Y inside the lane back into the bezier progress used by the border curves.
function solveProgressForLocalY(localY: number, height: number, curveY: number): number {
	const a = height - 2 * curveY;
	const b = 2 * curveY;
	const c = -localY;

	if (Math.abs(a) < 1e-6) {
		return Phaser.Math.Clamp(b !== 0 ? localY / b : 0, 0, 1);
	}

	const discriminant = b * b - 4 * a * c;
	const sqrtDiscriminant = Math.sqrt(Math.max(0, discriminant));
	const t1 = (-b + sqrtDiscriminant) / (2 * a);
	const t2 = (-b - sqrtDiscriminant) / (2 * a);

	if (t1 >= 0 && t1 <= 1) {
		return t1;
	}

	if (t2 >= 0 && t2 <= 1) {
		return t2;
	}

	return Phaser.Math.Clamp(Math.abs(t1 - 0.5) < Math.abs(t2 - 0.5) ? t1 : t2, 0, 1);
}

// Returns the tangent of one lane border at the requested progress so callers can build normals.
function getBoundaryTangent(metrics: LaneShapeMetrics, side: LaneSide, progress: number): Phaser.Math.Vector2 {
	const p0 = side === 'left' ? metrics.leftTop : metrics.rightTop;
	const p1 = side === 'left' ? metrics.leftControl : metrics.rightControl;
	const p2 = side === 'left' ? metrics.leftBottom : metrics.rightBottom;

	return new Phaser.Math.Vector2(
		2 * (1 - progress) * (p1.x - p0.x) + 2 * progress * (p2.x - p1.x),
		2 * (1 - progress) * (p1.y - p0.y) + 2 * progress * (p2.y - p1.y),
	);
}

// Builds a single source of truth for the lane bezier geometry so drawing and movement stay aligned.
export function createLaneShapeMetrics({
	width,
	topOffset,
	height,
	topWidthPct,
	bottomWidthPct,
}: {
	width: number;
	topOffset: number;
	height: number;
	topWidthPct: number;
	bottomWidthPct: number;
}): LaneShapeMetrics {
	const bottomWidth = Math.max(MIN_LANE_BOTTOM_WIDTH_PCT, bottomWidthPct);
	const unclampedTopWidth = Math.max(MIN_LANE_TOP_WIDTH_PCT, Math.min(MAX_LANE_TOP_WIDTH_PCT, topWidthPct));
	const topWidth = Math.min(unclampedTopWidth, Math.max(MIN_LANE_TOP_WIDTH_PCT, bottomWidth - 1));
	const bottomWidthPx = (width * bottomWidth) / 100;
	const topWidthPx = (width * topWidth) / 100;
	const halfBottom = bottomWidthPx / 2;
	const halfTop = topWidthPx / 2;
	const centerX = width / 2;
	const curveY = height * LANE_CURVE_DEPTH_RATIO;
	const controlOffset = Math.max(MIN_LANE_CONTROL_OFFSET, width * LANE_CONTROL_OFFSET_RATIO);
	const topY = topOffset;
	const bottomY = topOffset + height;

	const leftTop = new Phaser.Math.Vector2(centerX - halfTop, topY);
	const rightTop = new Phaser.Math.Vector2(centerX + halfTop, topY);
	const leftBottom = new Phaser.Math.Vector2(centerX - halfBottom, bottomY);
	const rightBottom = new Phaser.Math.Vector2(centerX + halfBottom, bottomY);
	const leftControl = new Phaser.Math.Vector2(centerX - (halfTop + halfBottom) / 2 - controlOffset, topY + curveY);
	const rightControl = new Phaser.Math.Vector2(centerX + (halfTop + halfBottom) / 2 + controlOffset, topY + curveY);

	return {
		width,
		height,
		topOffset,
		centerX,
		topWidthPx,
		bottomWidthPx,
		curveY,
		leftTop,
		rightTop,
		leftBottom,
		rightBottom,
		leftControl,
		rightControl,
		leftCurve: new Phaser.Curves.QuadraticBezier(leftTop, leftControl, leftBottom),
		rightCurve: new Phaser.Curves.QuadraticBezier(rightTop, rightControl, rightBottom),
	};
}

// Produces the polygon used to fill the full lane shape from the shared bezier metrics.
export function getLaneShapePoints(metrics: LaneShapeMetrics): Phaser.Geom.Point[] {
	return [
		new Phaser.Geom.Point(metrics.leftTop.x, metrics.leftTop.y),
		new Phaser.Geom.Point(metrics.rightTop.x, metrics.rightTop.y),
		...metrics.rightCurve
			.getPoints(LANE_CURVE_POINTS_COUNT)
			.slice(1)
			.map((point) => new Phaser.Geom.Point(point.x, point.y)),
		new Phaser.Geom.Point(metrics.leftBottom.x, metrics.leftBottom.y),
		...metrics.leftCurve
			.getPoints(LANE_CURVE_POINTS_COUNT)
			.slice(0, -1)
			.reverse()
			.map((point) => new Phaser.Geom.Point(point.x, point.y)),
	];
}

// Returns a point on one lane border plus an inward-facing normal for placing bank props near the edge.
export function getLaneBoundaryPoseAtY(
	metrics: LaneShapeMetrics,
	localY: number,
	side: LaneSide,
): {
	point: Phaser.Math.Vector2;
	inwardNormal: Phaser.Math.Vector2;
	progress: number;
} {
	const clampedLocalY = Phaser.Math.Clamp(localY, 0, metrics.height);
	const progress = solveProgressForLocalY(clampedLocalY, metrics.height, metrics.curveY);
	const curve = side === 'left' ? metrics.leftCurve : metrics.rightCurve;
	const tangent = getBoundaryTangent(metrics, side, progress).normalize();
	const inwardNormal =
		side === 'left'
			? new Phaser.Math.Vector2(tangent.y, -tangent.x)
			: new Phaser.Math.Vector2(-tangent.y, tangent.x);
	const point = curve.getPoint(progress);

	if (localY < 0) {
		return {
			point: new Phaser.Math.Vector2(point.x, metrics.topOffset + localY),
			inwardNormal: inwardNormal.normalize(),
			progress,
		};
	}

	if (localY > metrics.height) {
		return {
			point: new Phaser.Math.Vector2(point.x, metrics.topOffset + localY),
			inwardNormal: inwardNormal.normalize(),
			progress,
		};
	}

	return {
		point,
		inwardNormal: inwardNormal.normalize(),
		progress,
	};
}
